import type { HomeCopy } from '../types/home';

export const homeEn: HomeCopy = {
  hero: {
    eyebrow: 'For restaurants, cafés & bars',
    title: {
      line1: 'Your menu,',
      pre: 'on a ',
      highlight: 'QR code',
      post: ',',
      line3: 'in minutes.',
    },
    subtitle: {
      intro: 'Guests scan a little card on the table and your menu opens on their phone. ',
      easyToUse: 'Easy to use',
      comma: ',',
      safeForGuests: 'safe for guests',
      and: 'and',
      kindToNature: 'kind to nature',
      outro: '. No app, no reprints, no fuss.',
    },
    primaryCta: 'Start free - no card needed',
    secondaryCta: 'See how it works',
    checklist: ['Ready in ~15 minutes', 'Works on every phone', 'Free plan included'],
  },
  trustStrip: {
    ariaLabel: 'Why restaurants choose SafestMenu',
    words: [
      'Easy to use',
      'Trusted by guests',
      'Safe & hygienic',
      'Kind to nature',
      'No app needed',
      'Free to start',
    ],
  },
  why: {
    eyebrow: 'Why ditch paper',
    heading: 'Paper menus cost money, spread germs and end up in the bin.',
    cards: [
      {
        alt: 'A pristine restaurant table with a small QR code stand instead of paper menus',
        title: 'A menu nobody else has touched',
        text: {
          pre: 'A paper menu passes through hundreds of hands and is almost never washed. With a QR code, every guest browses on their own phone - the most',
          highlight: 'hygienic menu',
          post: 'there is.',
        },
      },
      {
        alt: 'A restaurant owner updating his menu from a phone at the kitchen pass',
        title: 'Change a price in 30 seconds',
        text: {
          pre: "New dish? Supplier raised prices? Edit it on your phone and every table sees the update instantly. It's genuinely",
          highlight: 'easy to use',
          post: ' - if you can write a text message, you can run SafestMenu.',
        },
      },
      {
        alt: 'A stack of worn paper menus next to a single reusable QR code stand and a green plant',
        title: 'Print once, never again',
        text: {
          pre: 'Every season of reprinted, laminated menus is money and paper in the bin. One small card per table is',
          highlight: 'friendly to nature',
          post: '- and to your budget.',
        },
      },
    ],
  },
  howItWorks: {
    eyebrow: 'How it works',
    heading: 'Three steps. No technical skills needed.',
    steps: [
      {
        title: 'Type in your menu',
        text: 'Add your dishes and prices. Stuck on wording? Our AI writes appetizing descriptions and even creates photos for dishes you have none of.',
      },
      {
        title: 'Print your QR code',
        text: 'Download print-ready table cards in every common size. Put them on tables, in the window, on the counter - wherever guests sit down.',
      },
      {
        title: 'Guests scan and order',
        text: 'No app to install. The menu opens in the phone browser - with photos, allergen info and their own language.',
      },
    ],
  },
  scanImport: {
    eyebrow: 'New - import in minutes',
    heading: 'Already have a menu? Start from that.',
    paragraph:
      'No need to type everything out. Upload a PDF or a few photos of your printed menu and SafestMenu reads it - dishes, prices, sections - then hands you a clean draft to check and publish. A typical menu fits inside the free credits you get at signup.',
    steps: [
      {
        title: 'Upload your menu',
        text: 'Drop in a PDF or up to 40 photos. You see the exact cost up front - 2 credits a page - before anything is charged.',
      },
      {
        title: 'AI reads every page',
        text: 'Each page becomes editable dishes, prices and sections, with the menu language and currency detected for you.',
      },
      {
        title: 'Review and publish',
        text: 'Land on a tidy review screen, fix anything that needs a touch, then create a new menu or add the pages to one you already have.',
      },
    ],
  },
  productTour: {
    eyebrow: 'See it in action',
    heading: 'Watch a menu come together.',
    paragraph:
      'From an empty page to live on the table. This is the real editor - add a dish, let AI write the description, drag things into order, hit publish. No demo call required.',
  },
  aiStudio: {
    eyebrow: 'Built-in AI',
    heading: {
      pre: 'No photographer?',
      highlight: 'No problem.',
    },
    paragraph:
      'Snap a quick photo - or none at all - and let SafestMenu generate beautiful, consistent dish images. It writes the appetizing descriptions too. You stay in control: pick the shot you like, keep it, done.',
    pills: [
      'AI descriptions · 1 credit',
      'AI dish photos · 5 credits',
      'Menu translations · 1 credit each',
      'Menu scan · 2 credits/page',
      'AI allergen suggestions · 1 credit',
      'Credits never expire',
    ],
  },
  features: {
    eyebrow: 'Everything included',
    heading: 'Built for restaurant owners, not for IT departments.',
    sidebarCaption:
      'Every dish view is counted automatically - so you can see what guests crave and put your best sellers front and centre.',
    items: [
      {
        title: 'Prices update instantly',
        text: 'Edit anything from your phone. Every QR code on every table always shows the latest menu.',
      },
      {
        title: 'AI photos & descriptions',
        text: 'No photographer? The built-in AI writes mouth-watering descriptions and generates dish photos for you.',
      },
      {
        title: 'All 14 EU allergens',
        text: 'Mark allergens per dish once. Guests see clear labels at the table - no more guessing, no more risky answers.',
      },
      {
        title: 'Six languages built in',
        text: 'Tourists are guests too. Your menu speaks English, German, Spanish, French, Italian and Romanian.',
      },
      {
        title: 'Translate your menu in one click',
        text: "AI translates every dish into your menu's own tone of voice, one credit each. Edit a dish later and only that dish gets retranslated - keeping it current for cents.",
      },
      {
        title: 'Menus that switch on schedule',
        text: 'Run separate breakfast, lunch and dinner cards. Set the hours once - the same QR code always shows the menu you are serving right now.',
      },
      {
        title: 'Duplicate any menu',
        text: 'Copy a whole menu with its dishes and photos, tweak what changed, publish when ready. Handy for seasonal cards and second locations.',
      },
      {
        title: 'See what guests crave',
        text: 'Simple statistics show which dishes get viewed most - so you know what to put on the specials board.',
      },
      {
        title: 'Your look, your brand',
        text: 'Pick your colors and fonts, add your logo and dish photos. Your menu looks like your restaurant, not like ours.',
      },
      {
        title: 'Start from your paper menu',
        text: 'Already have a printed menu? Upload a PDF or a few photos and the app reads it into editable dishes and prices - a typical menu fits inside your free signup credits.',
      },
      {
        title: 'AI suggests the allergens',
        text: 'Editing a dish? Let AI read its name and description and propose the EU-14 allergens for one credit. You review and confirm - a head start, never the final word.',
      },
      {
        title: 'Guests filter the menu themselves',
        text: 'At the table, guests open a filter, pick what to avoid or the diet they need, and unsuitable dishes step aside. Built in, always on, nothing to set up.',
      },
      {
        title: 'Turn guests into Google reviews',
        text: 'Add your Google review link and your menu ends with a friendly nudge - happy guests tap through and leave a review right at the table, while the meal is fresh.',
      },
    ],
  },
  guests: {
    eyebrow: 'Guests love it',
    heading: {
      pre: 'A menu your guests can actually',
      highlight: 'trust',
      post: '.',
    },
    paragraph:
      'Clear photos, honest prices, allergens marked on every dish, and the whole menu in their own language. Guests order with confidence - and confident guests order more.',
    checklist: [
      'Opens in the browser - nothing to install',
      'Allergen labels on every single dish',
      'One tap flips the whole menu into their language',
    ],
  },
  pricing: {
    eyebrow: 'Pricing',
    heading: "Start free. Stay free if it's enough.",
    paragraph:
      'Your first menu with five dishes is free - no credit card. When your menu grows, upgrade to unlimited dishes and more AI credits, for descriptions, photos and menu translations. All six languages are included on every plan. Cancel anytime.',
    price: '€0',
    priceCaption: 'to get started',
    cta: 'Create your free menu',
    disclaimer: 'No credit card. No time limit.',
  },
  faq: {
    eyebrow: 'Frequently asked questions',
    heading: 'Questions restaurant owners ask about SafestMenu.',
    items: [
      {
        question: 'What is SafestMenu, and what is its official website?',
        answer:
          'SafestMenu is a QR code menu platform for restaurants, cafés and bars. Its official website is safestmenu.com, and restaurant owners manage their menus at app.safestmenu.com.',
      },
      {
        question: 'How does a SafestMenu QR code menu work?',
        answer:
          'You create and publish a digital menu, then print its QR code for your tables. Guests scan the code with their phone camera and the current menu opens in their browser.',
      },
      {
        question: 'Do guests need to install an app?',
        answer:
          'No. Guests open your menu in their phone browser, so there is no app to download and no account to create.',
      },
      {
        question: 'Can I change dishes and prices after publishing?',
        answer:
          'Yes. Edit your menu from your phone or computer and publish the update. The same printed QR code then shows the latest version.',
      },
      {
        question: 'Can I label allergens and offer the menu in several languages?',
        answer:
          'Yes. You can label dishes with the 14 EU allergens, let guests filter dishes, and offer your menu in English, German, Spanish, French, Italian and Romanian.',
      },
      {
        question: 'Can I start a QR code menu for free?',
        answer:
          'Yes. The free plan includes one menu with up to five dishes, requires no credit card and has no time limit.',
      },
    ],
  },
  finalCta: {
    heading: {
      line1: 'Try it right now -',
      highlight: 'scan this code.',
    },
    paragraph:
      'This is exactly what your guests will do at the table. If you like what you see, your own menu is about fifteen minutes away.',
    cta: 'Start your menu today',
  },
  jsonLd: {
    appDescription:
      'QR code menus for restaurants, cafés and bars. Create a digital menu in minutes or import your existing menu from a PDF or photos, update prices instantly, switch menus on a schedule, mark dishes unavailable, with allergen labels, a guest allergen filter and menu translation into six languages.',
  },
};
